import { Button, IconButton } from "@zendeskgarden/react-buttons";
import { Field, MediaInput } from "@zendeskgarden/react-forms";
import { ThemeProvider } from "@zendeskgarden/react-theming";
import { ReactComponent as DownloadIcon } from "@zendeskgarden/svg-icons/src/16/download-stroke.svg";
import { ReactComponent as FilterIcon } from "@zendeskgarden/svg-icons/src/16/filter-stroke.svg";
import { ReactComponent as SearchIcon } from "@zendeskgarden/svg-icons/src/16/search-stroke.svg";
import { ReactComponent as XIcon } from "@zendeskgarden/svg-icons/src/16/x-stroke.svg";
import { ReactElement, useCallback, useEffect, useMemo } from "react";
import { useDataHealthComparisonQuery } from "../../../__generated__/generated_types";
import { CustomZDDropdown } from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import Loading from "../../../components/loading/Loadiing";
import useAppStore from "../../../hooks/useAppStore";
import { ViziblyTheme } from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import YearMonthSelector from "../../../components/year-month-selector/YearMonthSelector";
import { DataHealthTable } from "./components/DataHealthTable";
import { exportDataHealthCsv } from "./logic/csvExport";
import { isPropertyOutlier } from "./logic/outlierLogic";
import { sortRows } from "./logic/sortLogic";
import { PropertyDataHealthRow } from "./logic/types";
import { StatusFilter, useDataHealthReportState } from "./state/useDataHealthReportState";
import * as css from "./styles/dataHealthReport.module.scss";

const STATUS_PILLS: {value: StatusFilter; label: string}[] = [
    {value: "all", label: "All"},
    {value: "outliers", label: "Outliers only"},
    {value: "noChange", label: "No change"},
];

export function DataHealthReport(): ReactElement {
    const appStore = useAppStore();
    const state = useDataHealthReportState();

    useEffect(() => {
        appStore.set({isLoading: false});
    }, []);

    const canQuery = state.allowedBaseMonths.length > 0;

    const baseBounds = useMemo(() => {
        const first = state.allowedBaseMonths[0];
        const last = state.allowedBaseMonths[state.allowedBaseMonths.length - 1];
        return {
            minYear: first?.year,
            maxYear: last?.year,
            minMonth: first?.monthIndex,
            maxMonth: last?.monthIndex,
        };
    }, [state.allowedBaseMonths]);

    const comparisonBounds = useMemo(() => {
        const first = state.allowedComparisonMonths[0];
        const last = state.allowedComparisonMonths[state.allowedComparisonMonths.length - 1];
        return {
            minYear: first?.year,
            maxYear: last?.year,
            minMonth: first?.monthIndex,
            maxMonth: last?.monthIndex,
        };
    }, [state.allowedComparisonMonths]);

    const {data, loading, error} = useDataHealthComparisonQuery({
        variables: {
            baseYear: state.baseMonth.year,
            baseMonthIndex: state.baseMonth.monthIndex,
            comparisonYear: state.comparisonMonth.year,
            comparisonMonthIndex: state.comparisonMonth.monthIndex,
            propertyIds: state.selectedPropertyIds.length > 0 ? state.selectedPropertyIds : null,
        },
        skip: !canQuery,
        fetchPolicy: "cache-and-network",
    });

    const rows = useMemo(() => data?.dataHealthComparison ?? [], [data]);

    const isOutlier = useCallback(
        (r: PropertyDataHealthRow) => isPropertyOutlier(r, state.thresholds),
        [state.thresholds]
    );

    const outlierCount = useMemo(() => rows.filter(isOutlier).length, [rows, isOutlier]);

    const filteredRows = useMemo(() => {
        const q = state.search.trim().toLowerCase();
        return rows.filter(r => {
            if (q && !r.propertyName.toLowerCase().includes(q)) {
                return false;
            }
            if (state.statusFilter === "outliers" && !isOutlier(r)) {
                return false;
            }
            if (state.statusFilter === "noChange" && isOutlier(r)) {
                return false;
            }
            return true;
        });
    }, [rows, state.search, state.statusFilter, isOutlier]);

    const sortedRows = useMemo(
        () => sortRows(filteredRows, state.sortState, state.thresholds),
        [filteredRows, state.sortState, state.thresholds, state.baseMonth]
    );

    const onExportClicked = useCallback(() => {
        if (sortedRows.length === 0) return;
        exportDataHealthCsv(sortedRows, state.thresholds, state.baseMonth, state.comparisonMonth);
    }, [sortedRows, state.thresholds, state.baseMonth, state.comparisonMonth]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <main className={css.appShell}>
                <div className={css.content}>
                    <header className={css.pageHeading}>
                        <div className={css.pageHeadingTitles}>
                            <h1 className={css.pageTitle}>Data Health</h1>
                            <p className={css.subtitle}>Compare actual monthly data across your portfolio to identify unusual changes since your reforecast.</p>
                        </div>
                        <Button
                            onClick={onExportClicked}
                            disabled={sortedRows.length === 0}
                            aria-label="Export current view as CSV"
                        >
                            <Button.StartIcon>
                                <DownloadIcon />
                            </Button.StartIcon>
                            Export
                        </Button>
                    </header>

                    <section className={css.resultsSection}>
                        <div className={css.resultsHeader}>
                            <div className={css.resultsHeaderLeft}>
                                <div className={css.resultsHeaderTitle}>
                                    <h2 className={css.resultsTitle}>Compare Changes</h2>
                                    <p className={css.resultsSubtitle}>Review changes by property and metric.</p>
                                </div>
                                <Field className={css.searchField}>
                                    <MediaInput
                                        start={<SearchIcon />}
                                        end={state.search ? (
                                            <IconButton size="small" onClick={() => state.setSearch("")} aria-label="Clear search">
                                                <XIcon />
                                            </IconButton>
                                        ) : undefined}
                                        placeholder="Search properties"
                                        value={state.search}
                                        onChange={e => state.setSearch(e.target.value)}
                                        aria-label="Search properties"
                                    />
                                </Field>
                                <div className={css.statusPills}>
                                    {STATUS_PILLS.map(pill => {
                                        const isSelected = state.statusFilter === pill.value;
                                        const showCount = pill.value === "outliers" && outlierCount > 0;
                                        return (
                                            <button
                                                key={pill.value}
                                                type="button"
                                                className={isSelected ? css.pillSelected : undefined}
                                                onClick={() => state.setStatusFilter(pill.value)}
                                            >
                                                {pill.label}
                                                {showCount && <span className={css.pillCount}>{outlierCount}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className={css.resultsActions}>
                                <div className={css.filterControl}>
                                    <CustomZDDropdown
                                        initialSelectedItems={state.selectedPropertyIds}
                                        options={state.availableProperties}
                                        applySelectedItems={(items: string[]) => state.setSelectedPropertyIds(items)}
                                        openDropdownPlaceholder="All properties"
                                        closedDropdownPlaceholder={state.selectedPropertyIds.length === 0 ? "All properties" : "Properties"}
                                        isError={undefined}
                                        isMulti
                                        allOption
                                    />
                                </div>
                                <div className={css.filterControl}>
                                    <YearMonthSelector
                                        id="data-health-base-month"
                                        year={state.baseMonth.year}
                                        month={state.baseMonth.monthIndex}
                                        onClick={(year, monthIndex) => state.setBaseMonth({year, monthIndex})}
                                        minYear={baseBounds.minYear}
                                        maxYear={baseBounds.maxYear}
                                        minMonth={baseBounds.minMonth}
                                        maxMonth={baseBounds.maxMonth}
                                        showCalendarIcon
                                    />
                                </div>
                                <div className={css.filterControl}>
                                    <YearMonthSelector
                                        id="data-health-comparison-month"
                                        year={state.comparisonMonth.year}
                                        month={state.comparisonMonth.monthIndex}
                                        onClick={(year, monthIndex) => state.setComparisonMonth({year, monthIndex})}
                                        minYear={comparisonBounds.minYear}
                                        maxYear={comparisonBounds.maxYear}
                                        minMonth={comparisonBounds.minMonth}
                                        maxMonth={comparisonBounds.maxMonth}
                                        showCalendarIcon
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={css.resultsBody}>
                            {!canQuery && (
                                <div className={css.stateMessage}>
                                    <FilterIcon className={css.stateIcon} aria-hidden />
                                    Select a property from the top navigation to configure the base month.
                                </div>
                            )}
                            {canQuery && loading && rows.length === 0 && (
                                <div className={css.stateMessage}>
                                    <Loading />
                                </div>
                            )}
                            {canQuery && error && (
                                <div className={`${css.stateMessage} ${css.errorMessage}`}>
                                    Failed to load: {error.message}
                                </div>
                            )}
                            {canQuery && !error && !loading && rows.length === 0 && (
                                <div className={css.stateMessage}>
                                    No comparison data available for the selected months.
                                </div>
                            )}
                            {canQuery && sortedRows.length > 0 && (
                                <DataHealthTable
                                    rows={sortedRows}
                                    thresholds={state.thresholds}
                                    onThresholdChange={state.setThreshold}
                                    baseMonth={state.baseMonth}
                                    comparisonMonth={state.comparisonMonth}
                                    sortState={state.sortState}
                                    onSortChange={state.setSortState}
                                />
                            )}
                            {canQuery && !loading && !error && rows.length > 0 && sortedRows.length === 0 && (
                                <div className={css.stateMessage}>
                                    No properties match the current filters.
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </ThemeProvider>
    );
}
